import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Chart } from 'chart.js/auto';
import './LecturasHistoricas.css';
import { formatISO, subDays, subHours } from 'date-fns';

function LecturasHistoricas() {
  const usuario = JSON.parse(localStorage.getItem('usuario'));
  const claveGraficos = `graficos_${usuario.id}`;

  const [sensoresDisponibles, setSensoresDisponibles] = useState([]);
  const [graficos, setGraficos] = useState(() => {
    const guardados = localStorage.getItem(claveGraficos);
    return guardados ? JSON.parse(guardados) : [];
  });

  const [mostrarModal, setMostrarModal] = useState(false);
  const [nuevoGrafico, setNuevoGrafico] = useState({
    sensores: [],
    desde: '',
    hasta: '',
    titulo: ''
  });

  const [lecturasPorSensor, setLecturasPorSensor] = useState({});
  const [filtroTipo, setFiltroTipo] = useState(localStorage.getItem('filtroTipo') || '');
  const [filtroPiscicultura, setFiltroPiscicultura] = useState(localStorage.getItem('filtroPiscicultura') || '');

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get("https://iacloud.iapatagonia.cl/backend/sensores", {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => setSensoresDisponibles(res.data))
    .catch(err => console.error("Error cargando sensores:", err));
  }, []);

  useEffect(() => {
    async function cargarLecturasPersistidas() {
      const sensoresValidos = new Set(sensoresDisponibles.map(s => s.IdSensor));
      for (let grafico of graficos) {
        for (let sensorId of grafico.sensores) {
          if (sensoresValidos.has(parseInt(sensorId)) && !lecturasPorSensor[sensorId]) {
            await obtenerLecturas(sensorId, grafico.desde, grafico.hasta);
          }
        }
      }
    }
    if (graficos.length > 0 && sensoresDisponibles.length > 0) {
      cargarLecturasPersistidas();
    }
  }, [graficos, sensoresDisponibles]);

  const obtenerLecturas = async (sensorId, desde, hasta) => {
    try {
      const params = new URLSearchParams({ sensor_id: sensorId, desde, hasta });
      const res = await axios.get(`http://localhost:5000/lecturas/historicas?${params.toString()}`);
      const lecturas = res.data.map(l => ({
        x: new Date(l.FechaHora),
        y: l.Valor,
        tipo: l.Tipo,
        unidad: l.UnidadMedida,
        modelo: l.Modelo
      }));
      setLecturasPorSensor(prev => ({ ...prev, [sensorId]: lecturas }));
    } catch (error) {
      console.error("Error al obtener lecturas:", error);
      alert("No se pudieron obtener las lecturas del sensor.");
    }
  };

  const agregarGrafico = async () => {
    const { sensores, desde, hasta, titulo } = nuevoGrafico;
    if (sensores.length === 0 || !desde || !hasta || !titulo.trim()) return;
    if (new Date(desde) >= new Date(hasta)) {
      alert("La fecha inicial debe ser anterior a la fecha final.");
      return;
    }

    for (let id of sensores) {
      if (!lecturasPorSensor[id]) await obtenerLecturas(id, desde, hasta);
    }

    const nuevo = [...graficos, { sensores, desde, hasta, titulo }];
    setGraficos(nuevo);
    localStorage.setItem(claveGraficos, JSON.stringify(nuevo));

    setMostrarModal(false);
    setNuevoGrafico({ sensores: [], desde: '', hasta: '', titulo: '' });
  };

  const eliminarGrafico = (index) => {
    const nuevo = [...graficos];
    nuevo.splice(index, 1);
    setGraficos(nuevo);
    localStorage.setItem(claveGraficos, JSON.stringify(nuevo));
  };

  const sensoresFiltrados = sensoresDisponibles.filter(s => {
    const tipoOk = !filtroTipo || s.TipoSensor === filtroTipo;
    const piscOk = !filtroPiscicultura || s.Piscicultura === filtroPiscicultura;
    return tipoOk && piscOk;
  });

  useEffect(() => {
    localStorage.setItem('filtroTipo', filtroTipo);
    localStorage.setItem('filtroPiscicultura', filtroPiscicultura);
  }, [filtroTipo, filtroPiscicultura]);

  const colores = [
    '#00c4b3', '#ff6384', '#36a2eb', '#cc65fe', '#ffce56',
    '#2ecc71', '#f39c12', '#1abc9c', '#e74c3c', '#8e44ad'
  ];

  return (
    <>
      <Navbar nombre={usuario.nombre_completo} rol={usuario.rol} />
      <div className="pagina-historicas">
        <div className="top-bar">
          <h2>📊 Lecturas Históricas Personalizadas</h2>
          <button onClick={() => setMostrarModal(true)}>➕ Añadir gráfico</button>
        </div>

        {mostrarModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>➕ Añadir nuevo gráfico</h3>

              <input
                type="text"
                placeholder="Título del gráfico"
                value={nuevoGrafico.titulo}
                onChange={e => setNuevoGrafico({ ...nuevoGrafico, titulo: e.target.value })}
              />

              <select value={filtroPiscicultura} onChange={e => setFiltroPiscicultura(e.target.value)}>
                <option value="">-- Filtrar por piscicultura --</option>
                {[...new Set(sensoresDisponibles.map(s => s.Piscicultura))].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>

              <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">-- Filtrar por tipo --</option>
                {[...new Set(sensoresDisponibles.map(s => s.TipoSensor))].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <select multiple value={nuevoGrafico.sensores} onChange={e => {
                const opciones = Array.from(e.target.selectedOptions, o => o.value);
                setNuevoGrafico({ ...nuevoGrafico, sensores: opciones });
              }}>
                {sensoresFiltrados.map(s => (
                  <option key={s.IdSensor} value={s.IdSensor}>
                    [{s.IdSensor}] {s.TipoSensor} - {s.Modelo} ({s.Piscicultura})
                  </option>
                ))}
              </select>

              <input type="datetime-local" value={nuevoGrafico.desde} onChange={e => setNuevoGrafico({ ...nuevoGrafico, desde: e.target.value })} />
              <input type="datetime-local" value={nuevoGrafico.hasta} onChange={e => setNuevoGrafico({ ...nuevoGrafico, hasta: e.target.value })} />

              <div className="modal-presets">
                {[
                  { label: "30d", value: subDays(new Date(), 30) },
                  { label: "7d", value: subDays(new Date(), 7) },
                  { label: "24h", value: subHours(new Date(), 24) },
                  { label: "12h", value: subHours(new Date(), 12) },
                  { label: "1h", value: subHours(new Date(), 1) }
                ].map(p => (
                  <button key={p.label} onClick={() => setNuevoGrafico(prev => ({
                    ...prev,
                    desde: formatISO(p.value).slice(0, 16),
                    hasta: formatISO(new Date()).slice(0, 16)
                  }))}>{p.label}</button>
                ))}
              </div>

              <div className="modal-buttons">
                <button onClick={agregarGrafico}>Agregar</button>
                <button onClick={() => setMostrarModal(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        <div className="contenedor-graficos">
          {graficos.map((grafico, index) => (
            <div className="grafico-box" key={index}>
              <div className="grafico-header">
                <h4>{grafico.titulo}</h4>
                <button onClick={() => eliminarGrafico(index)}>❌</button>
              </div>
              <canvas
                ref={canvas => {
                  if (canvas) {
                    if (canvas.chartInstance) {
                      canvas.chartInstance.destroy();
                    }
                    canvas.chartInstance = new Chart(canvas, {
                      type: 'line',
                      data: {
                        datasets: grafico.sensores.map((sensorId, i) => ({
                          label: `Sensor ${sensorId}`,
                          data: lecturasPorSensor[sensorId] || [],
                          borderColor: colores[i % colores.length],
                          backgroundColor: colores[i % colores.length] + '33',
                          fill: true
                        }))
                      },
                      options: {
                        responsive: true,
                        scales: {
                          x: {
                            type: 'time',
                            time: {
                              tooltipFormat: 'dd-MM-yyyy HH:mm',
                              displayFormats: {
                                minute: 'HH:mm',
                                hour: 'HH:mm',
                                day: 'dd-MM'
                              }
                            },
                            title: { display: true, text: 'Fecha y Hora' }
                          },
                          y: {
                            title: { display: true, text: 'Valor' }
                          }
                        },
                        plugins: { legend: { display: true } }
                      }
                    });
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default LecturasHistoricas;
